import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface PropulsionType {
  name: string;
  impulse: string;
  thrust: string;
  color: string;
}

interface FuelType {
  name: string;
  isp: number;
  maxIsp: number;
  color: string;
}

interface FamousEngine {
  name: string;
  thrust: string;
  color: string;
}

interface StagingStep {
  label: string;
  icon: string;
}

interface NozzleZone {
  label: string;
  color: string;
}

interface RocketPropulsionTemplateProps {
  topic: string;
  subtitle: string;
  propulsionTypes: PropulsionType[];
  fuelTypes: FuelType[];
  famousEngines: FamousEngine[];
  staging: StagingStep[];
  nozzleZones: NozzleZone[];
  stats: { value: string; label: string }[];
  bottomInsight: string;
}

const DEFAULT_PROPS: RocketPropulsionTemplateProps = {
  topic: '火箭推进与航天动力',
  subtitle: 'Rocket Propulsion & Space Power',
  propulsionTypes: [
    {
      name: '化学推进',
      impulse: 'Isp 250-450s',
      thrust: '推力：极高',
      color: '#f97316',
    },
    {
      name: '电推进',
      impulse: 'Isp 1500-5000s',
      thrust: '推力：极低',
      color: '#3b82f6',
    },
    {
      name: '核热推进',
      impulse: 'Isp 800-1000s',
      thrust: '推力：中高',
      color: '#ef4444',
    },
    {
      name: '离子推进',
      impulse: 'Isp 3000-12000s',
      thrust: '推力：微牛级',
      color: '#a855f7',
    },
  ],
  fuelTypes: [
    { name: '液氢液氧', isp: 450, maxIsp: 500, color: '#3b82f6' },
    { name: '煤油液氧', isp: 310, maxIsp: 500, color: '#f97316' },
    { name: '固体推进剂', isp: 260, maxIsp: 500, color: '#94a3b8' },
    { name: '偏二甲肼', isp: 290, maxIsp: 500, color: '#fbbf24' },
  ],
  famousEngines: [
    { name: 'F-1 阿波罗', thrust: '680万N', color: '#f97316' },
    { name: 'Merlin 猎鹰', thrust: '84.5万N', color: '#3b82f6' },
    { name: 'RD-180', thrust: '390万N', color: '#ef4444' },
    { name: 'YF-77 长征', thrust: '51万N', color: '#fbbf24' },
  ],
  staging: [
    { label: '一级点火', icon: '🔥' },
    { label: '一级分离', icon: '✂️' },
    { label: '二级点火', icon: '💫' },
    { label: '载荷入轨', icon: '🛰️' },
  ],
  nozzleZones: [
    { label: '亚音速', color: '#3b82f6' },
    { label: '音速喉部', color: '#fbbf24' },
    { label: '超音速', color: '#ef4444' },
  ],
  stats: [
    { value: '680万N', label: 'F-1 单台推力' },
    { value: '可回收', label: '猎鹰9一级' },
    { value: '11.2km/s', label: '地球逃逸速度' },
  ],
  bottomInsight:
    '火箭科学的本质是动量守恒——以极高速度抛射质量，换取冲向星辰大海的力量',
};

export const RocketPropulsionTemplate: React.FC<
  Partial<RocketPropulsionTemplateProps>
> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const headerIn = spring({
    frame,
    fps,
    config: { damping: 18, stiffness: 80 },
  });

  const propStart = 60;
  const engineStart = 40;
  const fuelStart = 120;
  const stagingStart = 200;
  const nozzleStart = 280;
  const engineCardStart = 340;
  const orbitStart = 260;
  const statsStart = 420;
  const insightStart = 470;

  const exhaustFlow = interpolate(frame, [engineStart, engineStart + 300], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const nozzleDraw = interpolate(frame, [engineStart, engineStart + 50], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const insightOpacity = interpolate(
    frame,
    [insightStart, insightStart + 30],
    [0, 1],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
  );

  const orbitDraw = interpolate(frame, [orbitStart, orbitStart + 60], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const particleCount = 24;

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(135deg, #020210 0%, #0a0a1e 100%)',
        fontFamily:
          '-apple-system, "PingFang SC", "Noto Sans SC", sans-serif',
        color: 'white',
        overflow: 'hidden',
        width: 1920,
        height: 1080,
      }}
    >
      {/* Twinkling star dots */}
      {Array.from({ length: 60 }).map((_, i) => {
        const x = (i * 173 + 37) % 1920;
        const y = (i * 239 + 13) % 1080;
        const size = 1 + (i % 3);
        const twinkle = interpolate(
          (frame + i * 7) % 60,
          [0, 30, 59],
          [0.3, 1, 0.3],
          { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
        );
        return (
          <div
            key={`star-${i}`}
            style={{
              position: 'absolute',
              left: x,
              top: y,
              width: size,
              height: size,
              borderRadius: '50%',
              background: '#f8fafc',
              opacity: 0.06 * twinkle,
            }}
          />
        );
      })}

      {/* Header */}
      <div
        style={{
          position: 'absolute',
          top: 28,
          left: 44,
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          opacity: headerIn,
          transform: `translateY(${(1 - headerIn) * 16}px)`,
        }}
      >
        <div
          style={{
            display: 'inline-block',
            padding: '6px 20px',
            background: '#1a0a0a',
            borderRadius: 8,
            fontSize: 24,
            fontWeight: 700,
            color: '#f97316',
            border: '1px solid #f9731630',
            whiteSpace: 'nowrap',
          }}
        >
          🚀 PROPULSION
        </div>
        <h1
          style={{
            fontSize: 40,
            fontWeight: 900,
            margin: 0,
            color: '#f8fafc',
            whiteSpace: 'nowrap',
          }}
        >
          {p.topic}
        </h1>
        <p style={{ fontSize: 24, color: '#64748b', margin: 0, whiteSpace: 'nowrap' }}>
          {p.subtitle}
        </p>
      </div>

      {/* Key badge */}
      <div
        style={{
          position: 'absolute',
          top: 32,
          right: 44,
          textAlign: 'right',
          opacity: headerIn,
        }}
      >
        <div style={{ fontSize: 22, color: '#64748b' }}>核心公式</div>
        <div style={{ fontSize: 44, fontWeight: 900, color: '#f97316' }}>
          F = ṁ·Vₑ
        </div>
      </div>

      {/* ===== Left Panel: Propulsion Types ===== */}
      <div
        style={{ position: 'absolute', top: 130, left: 40, width: 370 }}
      >
        <div
          style={{
            fontSize: 26,
            color: '#64748b',
            marginBottom: 12,
            letterSpacing: '0.08em',
            fontWeight: 600,
          }}
        >
          推进方式
        </div>
        {p.propulsionTypes.map((pt, i) => {
          const s = spring({
            frame: frame - (propStart + i * 18),
            fps,
            config: { damping: 14, stiffness: 120 },
          });
          return (
            <div
              key={i}
              style={{
                background: '#0a0a1e',
                borderRadius: 12,
                padding: '12px 14px',
                marginBottom: 10,
                border: `1px solid ${pt.color}30`,
                opacity: s,
                transform: `translateX(${(1 - s) * -30}px)`,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div
                  style={{
                    fontSize: 24,
                    fontWeight: 800,
                    color: pt.color,
                  }}
                >
                  {pt.name}
                </div>
                <div
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: pt.color,
                    boxShadow: `0 0 10px ${pt.color}60`,
                  }}
                />
              </div>
              <div style={{ display: 'flex', gap: 14, marginTop: 2 }}>
                <div style={{ fontSize: 24, color: '#64748b' }}>
                  {pt.impulse}
                </div>
              </div>
              <div style={{ fontSize: 22, color: '#94a3b8', marginTop: 2 }}>
                {pt.thrust}
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== Center: Rocket Engine Cross-section SVG ===== */}
      <svg
        style={{ position: 'absolute', left: 430, top: 80 }}
        width="640"
        height="540"
        viewBox="0 0 520 440"
      >
        <defs>
          <linearGradient id="nozzleGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#94a3b8" />
            <stop offset="100%" stopColor="#475569" />
          </linearGradient>
          <linearGradient id="flameGrad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#fbbf24" />
            <stop offset="40%" stopColor="#f97316" />
            <stop offset="100%" stopColor="#ef4444" stopOpacity="0.3" />
          </linearGradient>
          <radialGradient id="combustionGrad">
            <stop offset="0%" stopColor="#fbbf24" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#f97316" stopOpacity="0.1" />
          </radialGradient>
        </defs>

        {/* Combustion chamber */}
        <rect
          x="120"
          y="155"
          width="100"
          height="100"
          rx="6"
          fill="none"
          stroke="#94a3b8"
          strokeWidth="2.5"
          opacity={nozzleDraw}
        />
        <ellipse
          cx="170"
          cy="205"
          rx="30"
          ry="30"
          fill="url(#combustionGrad)"
          opacity={nozzleDraw * 0.7}
        />
        <text
          x="170"
          y="295"
          textAnchor="middle"
          fill="#94a3b8"
          fontSize="18"
          fontWeight="600"
          opacity={nozzleDraw}
        >
          燃烧室
        </text>

        {/* Propellant injectors (top) */}
        {[145, 170, 195].map((x, i) => {
          const injOpacity = interpolate(
            frame,
            [engineStart + 20 + i * 8, engineStart + 35 + i * 8],
            [0, 0.8],
            { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
          );
          return (
            <g key={`inj-${i}`} opacity={injOpacity}>
              <line
                x1={x}
                y1="130"
                x2={x}
                y2="155"
                stroke="#3b82f6"
                strokeWidth="3"
                strokeLinecap="round"
              />
              <circle cx={x} cy="125" r="5" fill="#3b82f6" />
            </g>
          );
        })}
        <text
          x="170"
          y="110"
          textAnchor="middle"
          fill="#3b82f6"
          fontSize="20"
          fontWeight="600"
          opacity={nozzleDraw}
        >
          推进剂喷注器
        </text>

        {/* Convergent section (throat) */}
        <path
          d="M220,155 L260,185 L260,225 L220,255"
          fill="none"
          stroke="#94a3b8"
          strokeWidth="2.5"
          opacity={nozzleDraw}
        />

        {/* Throat label */}
        <line
          x1="260"
          y1="185"
          x2="260"
          y2="225"
          stroke="#fbbf24"
          strokeWidth="2"
          strokeDasharray="4 3"
          opacity={nozzleDraw * 0.6}
        />
        <text
          x="265"
          y="210"
          fill="#fbbf24"
          fontSize="18"
          fontWeight="600"
          opacity={nozzleDraw}
        >
          喉部
        </text>

        {/* Divergent section (bell nozzle) */}
        <path
          d="M260,185 L380,120 L380,290 L260,225"
          fill="none"
          stroke="url(#nozzleGrad)"
          strokeWidth="2.5"
          opacity={nozzleDraw}
        />

        {/* Exhaust plume expanding from nozzle */}
        <path
          d="M380,130 Q430,100 500,80 L500,330 Q430,310 380,280"
          fill="url(#flameGrad)"
          opacity={exhaustFlow * 0.5}
        />

        {/* Exhaust particles flowing outward */}
        {Array.from({ length: particleCount }).map((_, i) => {
          const localPhase = (exhaustFlow * 3 + i * 0.042) % 1;
          const baseY = 155 + (i * 11) % 100;
          const px = 380 + localPhase * 130;
          const spreadY =
            baseY + (baseY - 205) * localPhase * 0.8;
          const pOpacity = interpolate(
            localPhase,
            [0, 0.15, 0.7, 1],
            [0, 0.8, 0.4, 0],
            { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
          );
          const pColor =
            localPhase < 0.3
              ? '#fbbf24'
              : localPhase < 0.6
                ? '#f97316'
                : '#ef4444';
          return (
            <circle
              key={`part-${i}`}
              cx={px}
              cy={spreadY}
              r={2 + localPhase * 2}
              fill={pColor}
              opacity={pOpacity * nozzleDraw}
            />
          );
        })}

        {/* Thrust equation diagram */}
        <text
          x="260"
          y="340"
          textAnchor="middle"
          fill="#f8fafc"
          fontSize="22"
          fontWeight="800"
          opacity={interpolate(frame, [engineStart + 50, engineStart + 70], [0, 1], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          })}
        >
          F = ṁ · Vₑ + (Pₑ − P₀)·Aₑ
        </text>
        <text
          x="260"
          y="370"
          textAnchor="middle"
          fill="#64748b"
          fontSize="18"
          opacity={interpolate(frame, [engineStart + 60, engineStart + 80], [0, 0.8], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          })}
        >
          推力 = 质量流率×排气速度 + 压力差×喷口面积
        </text>

        {/* Animated variable highlights */}
        {[
          { text: 'ṁ', x: 195, y: 400, delay: 70, color: '#3b82f6' },
          { text: 'Vₑ', x: 260, y: 400, delay: 85, color: '#f97316' },
          { text: 'Aₑ', x: 330, y: 400, delay: 100, color: '#94a3b8' },
        ].map((v, i) => {
          const vIn = spring({
            frame: frame - (engineStart + v.delay),
            fps,
            config: { damping: 12, stiffness: 150 },
          });
          return (
            <g key={`var-${i}`} opacity={vIn}>
              <rect
                x={v.x - 22}
                y={v.y - 15}
                width="44"
                height="22"
                rx="6"
                fill={`${v.color}20`}
                stroke={v.color}
                strokeWidth="1"
              />
              <text
                x={v.x}
                y={v.y}
                textAnchor="middle"
                fill={v.color}
                fontSize="17"
                fontWeight="700"
              >
                {v.text}
              </text>
            </g>
          );
        })}
      </svg>

      {/* ===== Right Panel: Fuel Types with Isp bars ===== */}
      <div
        style={{ position: 'absolute', top: 175, right: 40, width: 380 }}
      >
        <div
          style={{
            fontSize: 26,
            color: '#64748b',
            marginBottom: 12,
            letterSpacing: '0.08em',
            fontWeight: 600,
          }}
        >
          燃料类型 · 比冲对比
        </div>
        {p.fuelTypes.map((ft, i) => {
          const s = spring({
            frame: frame - (fuelStart + i * 18),
            fps,
            config: { damping: 14, stiffness: 120 },
          });
          const barWidth = interpolate(
            frame,
            [fuelStart + i * 18, fuelStart + i * 18 + 30],
            [0, (ft.isp / ft.maxIsp) * 100],
            { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
          );
          return (
            <div
              key={i}
              style={{
                background: '#0a0a1e',
                borderRadius: 12,
                padding: '12px 14px',
                marginBottom: 10,
                border: `1px solid ${ft.color}30`,
                opacity: s,
                transform: `translateX(${(1 - s) * 30}px)`,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 6,
                }}
              >
                <div
                  style={{
                    fontSize: 24,
                    fontWeight: 800,
                    color: ft.color,
                  }}
                >
                  {ft.name}
                </div>
                <div
                  style={{
                    fontSize: 20,
                    fontWeight: 700,
                    color: '#94a3b8',
                  }}
                >
                  Isp {ft.isp}s
                </div>
              </div>
              <div
                style={{
                  height: 10,
                  background: '#1e293b',
                  borderRadius: 5,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${barWidth}%`,
                    height: '100%',
                    background: ft.color,
                    borderRadius: 4,
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== Staging Animation ===== */}
      <div
        style={{
          position: 'absolute',
          top: 780,
          left: 40,
          width: 510,
        }}
      >
        <div
          style={{
            fontSize: 26,
            color: '#64748b',
            marginBottom: 14,
            letterSpacing: '0.08em',
            fontWeight: 600,
          }}
        >
          多级火箭分离序列
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 0,
          }}
        >
          {p.staging.map((step, i) => {
            const s = spring({
              frame: frame - (stagingStart + i * 22),
              fps,
              config: { damping: 12, stiffness: 130 },
            });
            const isLast = i === p.staging.length - 1;
            return (
              <React.Fragment key={i}>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    opacity: s,
                    transform: `scale(${s})`,
                  }}
                >
                  <div
                    style={{
                      width: 62,
                      height: 62,
                      borderRadius: '50%',
                      background: '#0a0a1e',
                      border: '2px solid #f97316',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 30,
                    }}
                  >
                    {step.icon}
                  </div>
                  <div
                    style={{
                      fontSize: 20,
                      fontWeight: 700,
                      color: '#f97316',
                      marginTop: 6,
                      textAlign: 'center',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {step.label}
                  </div>
                </div>
                {!isLast && (
                  <div
                    style={{
                      width: 40,
                      height: 2,
                      background:
                        'linear-gradient(90deg, #f97316, #f9731640)',
                      margin: '0 4px',
                      marginBottom: 22,
                      opacity: interpolate(
                        frame,
                        [
                          stagingStart + i * 22 + 10,
                          stagingStart + i * 22 + 25,
                        ],
                        [0, 0.7],
                        {
                          extrapolateLeft: 'clamp',
                          extrapolateRight: 'clamp',
                        },
                      ),
                    }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* ===== Nozzle Design: Convergent-Divergent ===== */}
      <div
        style={{
          position: 'absolute',
          top: 640,
          left: 600,
          width: 440,
        }}
      >
        <div
          style={{
            fontSize: 26,
            color: '#64748b',
            marginBottom: 12,
            letterSpacing: '0.08em',
            fontWeight: 600,
          }}
        >
          喷管设计 · 收敛-扩散
        </div>
        <svg width="440" height="175" viewBox="0 0 360 140">
          {/* Nozzle outline */}
          <path
            d="M20,20 L120,55 L120,85 L20,120"
            fill="none"
            stroke="#94a3b8"
            strokeWidth="2"
            opacity={interpolate(frame, [nozzleStart, nozzleStart + 30], [0, 1], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            })}
          />
          <path
            d="M120,55 L340,10 L340,130 L120,85"
            fill="none"
            stroke="#94a3b8"
            strokeWidth="2"
            opacity={interpolate(frame, [nozzleStart + 15, nozzleStart + 45], [0, 1], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            })}
          />

          {/* Flow zone fills */}
          <path
            d="M20,20 L120,55 L120,85 L20,120 Z"
            fill="#3b82f620"
            opacity={interpolate(frame, [nozzleStart + 20, nozzleStart + 40], [0, 0.6], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            })}
          />
          <path
            d="M120,55 L340,10 L340,130 L120,85 Z"
            fill="#ef444420"
            opacity={interpolate(frame, [nozzleStart + 30, nozzleStart + 50], [0, 0.6], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            })}
          />

          {/* Throat line */}
          <line
            x1="120"
            y1="50"
            x2="120"
            y2="90"
            stroke="#fbbf24"
            strokeWidth="2"
            strokeDasharray="5 3"
            opacity={interpolate(frame, [nozzleStart + 25, nozzleStart + 40], [0, 0.8], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            })}
          />

          {/* Zone labels */}
          {p.nozzleZones.map((z, i) => {
            const positions = [
              { x: 65, y: 84 },
              { x: 120, y: 115 },
              { x: 240, y: 84 },
            ];
            const zOpacity = interpolate(
              frame,
              [nozzleStart + 30 + i * 12, nozzleStart + 45 + i * 12],
              [0, 1],
              { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
            );
            return (
              <text
                key={i}
                x={positions[i].x}
                y={positions[i].y}
                textAnchor="middle"
                fill={z.color}
                fontSize="18"
                fontWeight="700"
                opacity={zOpacity}
              >
                {z.label}
              </text>
            );
          })}

          {/* Mach number arrows */}
          <text
            x="65"
            y="60"
            textAnchor="middle"
            fill="#3b82f6"
            fontSize="18"
            opacity={interpolate(frame, [nozzleStart + 40, nozzleStart + 55], [0, 0.8], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            })}
          >
            M &lt; 1
          </text>
          <text
            x="120"
            y="135"
            textAnchor="middle"
            fill="#fbbf24"
            fontSize="18"
            opacity={interpolate(frame, [nozzleStart + 45, nozzleStart + 60], [0, 0.8], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            })}
          >
            M = 1
          </text>
          <text
            x="240"
            y="60"
            textAnchor="middle"
            fill="#ef4444"
            fontSize="18"
            opacity={interpolate(frame, [nozzleStart + 50, nozzleStart + 65], [0, 0.8], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            })}
          >
            M &gt; 1
          </text>
        </svg>
      </div>

      {/* ===== Hohmann Transfer Orbit ===== */}
      <div
        style={{
          position: 'absolute',
          top: 570,
          right: 150,
          width: 500,
        }}
      >
        <div
          style={{
            fontSize: 26,
            color: '#64748b',
            marginBottom: 12,
            letterSpacing: '0.08em',
            fontWeight: 600,
          }}
        >
          霍曼转移轨道
        </div>
        <svg width="500" height="300" viewBox="0 0 320 180">
          {/* Earth (central body) */}
          <circle
            cx="110"
            cy="90"
            r="16"
            fill="#3b82f6"
            opacity={orbitDraw}
          />
          <text
            x="110"
            y="95"
            textAnchor="middle"
            fill="#f8fafc"
            fontSize="13"
            fontWeight="700"
            opacity={orbitDraw}
          >
            地球
          </text>

          {/* Low orbit (inner circle) */}
          <circle
            cx="110"
            cy="90"
            r="45"
            fill="none"
            stroke="#3b82f6"
            strokeWidth="1.5"
            strokeDasharray="4 3"
            opacity={orbitDraw * 0.6}
          />

          {/* High orbit (outer circle) */}
          <circle
            cx="110"
            cy="90"
            r="80"
            fill="none"
            stroke="#a855f7"
            strokeWidth="1.5"
            strokeDasharray="4 3"
            opacity={orbitDraw * 0.6}
          />

          {/* Transfer ellipse (half) */}
          <path
            d="M155,90 A62.5,50 0 0,1 30,90"
            fill="none"
            stroke="#f97316"
            strokeWidth="2.5"
            strokeDasharray="200"
            strokeDashoffset={200 * (1 - orbitDraw)}
            opacity={0.8}
          />

          {/* Burn point 1 */}
          <circle
            cx="155"
            cy="90"
            r="8"
            fill="#ef4444"
            opacity={interpolate(frame, [orbitStart + 20, orbitStart + 35], [0, 1], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            })}
          />
          <text
            x="155"
            y="115"
            textAnchor="middle"
            fill="#ef4444"
            fontSize="15"
            fontWeight="700"
            opacity={interpolate(frame, [orbitStart + 25, orbitStart + 40], [0, 1], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            })}
          >
            Δv₁ 加速
          </text>

          {/* Burn point 2 */}
          <circle
            cx="30"
            cy="90"
            r="8"
            fill="#22c55e"
            opacity={interpolate(frame, [orbitStart + 40, orbitStart + 55], [0, 1], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            })}
          />
          <text
            x="30"
            y="73"
            textAnchor="middle"
            fill="#22c55e"
            fontSize="15"
            fontWeight="700"
            opacity={interpolate(frame, [orbitStart + 45, orbitStart + 60], [0, 1], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            })}
          >
            Δv₂ 入轨
          </text>

          {/* Labels */}
          <text
            x="240"
            y="45"
            fill="#a855f7"
            fontSize="16"
            fontWeight="700"
            opacity={orbitDraw}
          >
            目标轨道
          </text>
          <text
            x="240"
            y="90"
            fill="#3b82f6"
            fontSize="16"
            fontWeight="700"
            opacity={orbitDraw}
          >
            初始轨道
          </text>
          <text
            x="240"
            y="135"
            fill="#f97316"
            fontSize="16"
            fontWeight="700"
            opacity={orbitDraw}
          >
            转移轨道
          </text>

          {/* Color legend lines */}
          {[
            { y: 42, color: '#a855f7' },
            { y: 87, color: '#3b82f6' },
            { y: 132, color: '#f97316' },
          ].map((l, i) => (
            <line
              key={`leg-${i}`}
              x1={i === 0 ? interpolate(orbitDraw, [0, 0.5], [199, 213], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }) : 213}
              y1={l.y}
              x2={i === 0 ? interpolate(orbitDraw, [0, 0.5], [199, 227], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }) : 227}
              y2={l.y}
              stroke={l.color}
              strokeWidth="2.5"
              opacity={orbitDraw}
            />
          ))}
        </svg>
      </div>

      {/* ===== Famous Engines Cards (vertical, left of fuel panel) ===== */}
      <div
        style={{
          position: 'absolute',
          top: 175,
          right: 455,
          width: 320,
        }}
      >
        <div
          style={{
            fontSize: 26,
            color: '#64748b',
            marginBottom: 12,
            letterSpacing: '0.08em',
            fontWeight: 600,
          }}
        >
          经典发动机
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {p.famousEngines.map((eng, i) => {
            const s = spring({
              frame: frame - (engineCardStart + i * 16),
              fps,
              config: { damping: 14, stiffness: 110 },
            });
            return (
              <div
                key={i}
                style={{
                  background: '#0a0a1e',
                  borderRadius: 12,
                  padding: '12px 14px',
                  borderLeft: `3px solid ${eng.color}`,
                  opacity: s,
                  transform: `translateX(${(1 - s) * -30}px)`,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div
                    style={{
                      fontSize: 24,
                      fontWeight: 800,
                      color: '#f8fafc',
                    }}
                  >
                    {eng.name}
                  </div>
                  <div
                    style={{
                      fontSize: 24,
                      fontWeight: 900,
                      color: eng.color,
                    }}
                  >
                    {eng.thrust}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ===== Stats ===== */}
      {/* <div
        style={{
          position: 'absolute',
          bottom: 105,
          left: 40,
          right: 40,
          display: 'flex',
          gap: 16,
        }}
      >
        {p.stats.map((st, i) => {
          const stIn = spring({
            frame: frame - (statsStart + i * 14),
            fps,
            config: { damping: 14, stiffness: 100 },
          });
          return (
            <div
              key={i}
              style={{
                flex: 1,
                background: '#0a0a1e',
                borderRadius: 12,
                padding: '14px 10px',
                textAlign: 'center',
                border: '1px solid #1e293b',
                opacity: stIn,
                transform: `scale(${0.8 + 0.2 * stIn})`,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'baseline',
                  justifyContent: 'center',
                  gap: 8,
                }}
              >
                <div
                  style={{
                    fontSize: 30,
                    fontWeight: 900,
                    color: '#f97316',
                  }}
                >
                  {st.value}
                </div>
                <div
                  style={{
                    fontSize: 22,
                    color: '#64748b',
                  }}
                >
                  {st.label}
                </div>
              </div>
            </div>
          );
        })}
      </div> */}

      {/* ===== Bottom Insight (OPAQUE background) ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 8,
          left: 40,
          right: 40,
          background: '#0a0a1e',
          borderRadius: 14,
          padding: '14px 20px',
          borderTop: '3px solid #f97316',
          opacity: insightOpacity,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 30 }}>🚀</span>
          <span
            style={{
              fontSize: 22,
              color: '#e2e8f0',
              lineHeight: 1.5,
            }}
          >
            {p.bottomInsight}
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 510], [0, 100], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          })}%`,
          height: 4,
          background:
            'linear-gradient(90deg, #f97316, #fbbf24, #ef4444, #3b82f6)',
        }}
      />
    </AbsoluteFill>
  );
};
